/** Turning a `NewGameScreen` setup into a `GameState`.
 *
 * The map comes from `@game/content`'s generator and the state from the
 * kernel's `newGame`; nothing here decides a rule. The one judgement it makes
 * is which hero definition a faction starts with, and that is a scenario
 * choice, not a rule - it is deterministic in the seed so a replay of the same
 * setup produces the same game.
 */

import type {
  ContentRegistry, FactionId, GameState, HeroDefId, NewGameSetup, PlayerId, PlayerSetup,
} from "@game/core";
import { asPlayerId, cellIndex, inScoutingDisc, newGame } from "@game/core";
import type { GeneratedMap } from "@game/content/mapgen";
import { generateMap, toObjectSetups } from "@game/content/mapgen";
import { startingResourcesFor } from "./controllers.js";

/** The eight the classic player colours, in their canonical order. */
export const PLAYER_COLOURS: readonly string[] = Object.freeze([
  "red", "blue", "tan", "green", "orange", "purple", "teal", "pink",
]);

export interface GameOptions {
  readonly seed: number;
  /** Cells on a side. The New Game screen offers 36 / 72 / 108 / 144. */
  readonly mapCells: number;
  readonly faction: string;
  /** Kingdoms other than the first. Some may be human; see `humans`. */
  readonly opponents: number;
  /**
   * How many of the kingdoms are played by people at this machine.
   *
   * 1 is the ordinary game and is the default. Above that is hotseat: the
   * first `humans` seats are people and the rest are computers, and the shell
   * passes the machine between them at the turn boundary. The kernel has
   * always supported it - `PlayerState.kind` is per player and `newGame` never
   * cared how many were `human` - and the only thing that was hard-wired to
   * one was the *host*: a single `HumanController` and a single viewer.
   */
  readonly humans?: number;
  /** 0..4, as the classic numbers them. Only the AI reads it. */
  readonly difficulty: number;
  /**
   * Cells of ground each player's home is scouted out to before the first turn.
   *
   * A scenario choice, not a rule: `BASE_SCOUTING_RADIUS` is what a hero *sees*,
   * and at 5 it lights a 11-cell disc. A 1080p map viewport is 25.8 by 15.6
   * cells, so a fresh game opened on a hole of land with black on all four
   * sides and three quarters of the screen empty - which is not what the classic
   * shows on day one, where the starting valley runs off the frame. Revealed
   * ground is written REMEMBERED, not VISIBLE, so it is dimmed rather than lit
   * and nothing about who owns what leaks: the player has "been here before",
   * which is exactly what a home region is.
   */
  readonly startingReveal?: number;
  /**
   * The kernel's `NewGameSetup.victory`, chosen on the New Game screen.
   *
   * Passed through untouched: `newGame` is what decides whether the condition
   * can be honoured on the map it has just built, and it rewrites the string it
   * cannot (`resolveVictoryCondition`). Deciding that here would be a second
   * implementation of the rule.
   */
  readonly victory?: string;
}

/** Wide enough that the home valley runs off every edge of a 1080p viewport
 *  (25.8 x 15.6 cells) while its corners stay unexplored. */
export const DEFAULT_STARTING_REVEAL = 14;

export const DEFAULT_OPTIONS: GameOptions = Object.freeze({
  seed: 20260823, mapCells: 72, faction: "sunspire", opponents: 1, difficulty: 1,
  humans: 1, startingReveal: DEFAULT_STARTING_REVEAL, victory: "defeat_all",
});

/** Seats a person can take. Eight is the map's own limit. */
export const MAX_HUMANS = 8;

/** How many seats are people, clamped to what the map actually has. */
export const humanSeats = (opts: GameOptions): number => {
  const players = Math.max(2, Math.min(8, opts.opponents + 1));
  const want = Math.trunc(opts.humans ?? 1);
  return want < 1 ? 1 : want > players ? players : want;
};

export interface StartedGame {
  readonly state: GameState;
  readonly generated: GeneratedMap;
  readonly players: readonly PlayerId[];
  readonly human: PlayerId;
  readonly options: GameOptions;
}

/** The faction's roster, sorted so a seed maps to the same face every time. */
export function heroesOfFaction(content: ContentRegistry, faction: string): HeroDefId[] {
  const ids: HeroDefId[] = [];
  for (const h of content.heroes.values()) {
    if ((h.faction as string) === faction) ids.push(h.id);
  }
  ids.sort();
  return ids;
}

/**
 * The hero a faction starts with.
 *
 * With no seed this is the alphabetically-first face, which is what the editor
 * wants: a draft names a faction, not a person. With a seed it is a roster
 * draw, deterministic in that seed, skipping faces already taken so two
 * Gravewatch seats do not open as the same bone sage. The same seed always opens
 * on the same face.
 */
export function startingHero(
  content: ContentRegistry,
  faction: string,
  seed?: number,
  taken?: ReadonlySet<string>,
): HeroDefId {
  const ids = heroesOfFaction(content, faction);
  if (ids.length === 0) throw new Error(`no hero definition for faction ${faction}`);
  const free = taken === undefined ? ids : ids.filter((id) => !taken.has(id as string));
  const pool = free.length > 0 ? free : ids;
  if (seed === undefined) return pool[0]!;
  return pool[Math.abs(seed) % pool.length]!;
}

/** Factions with a full roster - never the roster-less `neutral`. */
export function playableFactions(content: ContentRegistry): readonly string[] {
  const out: string[] = [];
  for (const f of content.factions.values()) {
    if (f.creatures.length === 0 || f.buildings.length === 0) continue;
    out.push(f.id as string);
  }
  out.sort();
  return out;
}

/**
 * Town names, per faction. A town with no name shows its own id ("town:1") in
 * every panel that names it, which reads as a missing string rather than as a
 * town. `TownSetup.name` is a scenario field, not a rule, so the choice lives
 * here and is deterministic in the seed.
 */
const TOWN_NAMES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  sunspire: ["Whitestone", "Dawnmere", "Brightwall", "Gravewood", "Lionhall", "Kingsmarch"],
  greenmantle: ["Elmshade", "Mossbrook", "Silverleaf", "Greenhollow", "Thornwood", "Dawnglade"],
  skyreach: ["Loftmere", "Cloudspire", "Celeste", "Highvale", "Frostglass", "Starcrest"],
  emberpit: ["Emberfall", "Ashgate", "Soothollow", "Cinderhold", "Brimstone", "Scorchmoor"],
  gravewatch: ["Duskbarrow", "Gravemoor", "Boneyard", "Mourncrest", "Sepulchre", "Ashenvale"],
  deepvault: ["Undermere", "Blackreach", "Shadowdeep", "Underhall", "Gloomspire", "Wyrmroost"],
  ironhorde: ["Tuskfall", "Bloodrock", "Skullcrag", "Warcamp", "Ironjaw", "Redridge"],
  fenmire: ["Reedholm", "Mirefen", "Blackwater", "Bogholm", "Reedmarch", "Serpentmire"],
  prismgate: ["Elementia", "Stormhaven", "Windreach", "Prismgate", "Aetherfall", "Riftspire"],
  saltreach: ["Brinehaven", "Saltbeard", "Tidewatch", "Blackreef", "Reavers Rest", "Gullport"],
  cogworks: ["Gearford", "Coldwater", "Ironworks", "Steamhold", "Rustgate", "Pinnacle"],
  frostmark: ["Rimeholt", "Frostvale", "Hearthstead", "Rimehold", "Stagmoor", "Snowmere"],
  blackwater: ["Nixmoor", "Drownfen", "Silthaven", "Kelpgate", "Mirewatch", "Gloamdelta"],
});

/** Deterministic in the seed. Six names per faction, so a map with more than
 *  six players of one faction would repeat one; no map size allows that.
 *
 *  Exported for the screenshot fixtures, which used to fall back to the
 *  faction's own display name and so printed "Castle" where every real town
 *  screen prints the town's name. */
export function townName(faction: string, index: number, seed: number): string {
  const pool = TOWN_NAMES[faction];
  if (pool === undefined || pool.length === 0) return `${faction} town`;
  return pool[(index + seed) % pool.length] ?? pool[0]!;
}

export function startGame(content: ContentRegistry, opts: GameOptions): StartedGame {
  const players = Math.max(2, Math.min(8, opts.opponents + 1));
  // 288 is the generator's own ceiling (`mapgen/generate.ts`); this clamp is a
  // second copy of it and has to move with it, or the setup screen offers a
  // Massive map and quietly builds an Extra Large one.
  const size = Math.max(24, Math.min(288, opts.mapCells));

  // The human's faction is pinned; the generator rolls the rest. Passing only
  // the first entry would make the array length disagree with `players`, so the
  // remaining slots are filled from the playable set, deterministically.
  const pool = playableFactions(content);
  const wanted: FactionId[] = [opts.faction as FactionId];
  for (let i = 1; i < players; i++) {
    const pick = pool[(opts.seed + i * 7) % pool.length];
    wanted.push((pick ?? "sunspire") as FactionId);
  }

  const gen = generateMap(opts.seed, {
    content, width: size, height: size, players, factions: wanted,
  });

  const ids = gen.startingPositions.map((_, i) => asPlayerId(`p${String(i + 1)}`));
  const humans = humanSeats(opts);
  const takenHeroes = new Set<string>();
  const setups: PlayerSetup[] = gen.startingPositions.map((s, i) => {
    const town = gen.towns[s.town];
    if (town === undefined) throw new Error(`generated map lost town ${String(s.town)}`);
    const hero = startingHero(content, s.faction as string, opts.seed + i * 31, takenHeroes);
    takenHeroes.add(hero as string);
    return {
      id: ids[i]!,
      kind: i < humans ? "human" : "ai",
      colour: PLAYER_COLOURS[i] ?? "red",
      faction: s.faction,
      // Difficulty is the human's handicap and nothing else: every person at
      // the machine is put on the purse his level names, and every computer is
      // left on `newGame`'s default, which is the Normal purse. In a hotseat
      // game that means the people share a handicap and the computers do not
      // have one, which is the only reading that keeps the level meaning the
      // same thing it means in a one-player game.
      ...(i < humans ? { resources: startingResourcesFor(opts.difficulty) } : {}),
      towns: [{
        faction: s.faction, name: townName(s.faction as string, i, opts.seed),
        x: town.x, y: town.y, z: town.z,
        // The generator decides the opening defence; a capital that ships
        // without one is a capital the first hero to walk in owns.
        garrison: town.garrison,
      }],
      heroes: [{ def: hero, x: s.heroX, y: s.heroY, z: s.heroZ }],
    };
  });

  // The generator's unowned towns, with the garrison and the build list it
  // sized by distance from the nearest start. Without these a neutral town is
  // a map object a hero walks over and is told nothing about.
  const neutralTowns = gen.towns
    .filter((t) => t.player < 0)
    .map((t, i) => ({
      faction: t.faction, name: townName(t.faction as string, players + i, opts.seed),
      x: t.x, y: t.y, z: t.z,
      built: t.built, garrison: t.garrison,
    }));

  const setup: NewGameSetup = {
    rootSeed: opts.seed,
    map: gen.map,
    players: setups,
    neutralTowns,
    objects: toObjectSetups(gen),
    victory: opts.victory ?? "defeat_all",
  };

  const state = newGame(setup, content);
  revealHomeRegions(state, setups, opts.startingReveal ?? DEFAULT_STARTING_REVEAL);

  return {
    state,
    generated: gen,
    players: ids,
    human: ids[0] ?? asPlayerId("p1"),
    options: opts,
  };
}

/**
 * Mark the disc around each player's own town and hero as explored.
 *
 * Written straight into the fog plane rather than through `revealAround`,
 * because the scenario wants REMEMBERED ground - explored, not watched. Lighting
 * it VISIBLE would hand the player live sight of a fifteen-cell circle on turn
 * one, which would show enemy heroes and mine ownership the hero cannot see.
 * Only cells that are still unseen are touched, so the real sight discs the
 * kernel just computed keep their VISIBLE state.
 */
function revealHomeRegions(
  state: GameState, setups: readonly PlayerSetup[], radius: number,
): void {
  if (radius <= 0) return;
  const map = state.map;
  const r = Math.trunc(radius);
  for (const p of setups) {
    const fog = state.fog[p.id];
    if (fog === undefined) continue;
    const centres: { x: number; y: number; z: number }[] = [];
    for (const t of p.towns ?? []) centres.push({ x: t.x, y: t.y, z: t.z });
    for (const h of p.heroes ?? []) centres.push({ x: h.x, y: h.y, z: h.z });
    for (const c of centres) {
      const y0 = Math.max(0, c.y - r), y1 = Math.min(map.height - 1, c.y + r);
      const x0 = Math.max(0, c.x - r), x1 = Math.min(map.width - 1, c.x + r);
      for (let cy = y0; cy <= y1; cy++) {
        for (let cx = x0; cx <= x1; cx++) {
          if (!inScoutingDisc(cx - c.x, cy - c.y, r)) continue;
          const i = cellIndex(map, cx, cy, c.z);
          if (fog.seen[i] === 0) fog.seen[i] = 1;
        }
      }
    }
  }
}
